/**
 * @component TermsComponent
 * @title 약관 팝업
 * @description
 * @param {Props} [props] 필요한 경우에만 Props 요약을 명시합니다.
 * @author 2034263
 * @history
 *   - [2025-09-30] 초기 작성
 */

import DOMPurify from 'dompurify';
import { memo, useCallback, useRef, useState } from 'react';

import { DialogWrapper, RoundButton } from 'scbk-ui-web';

import { useTranslation } from 'react-i18next';
import { Envs, useBackKeyInterceptor, useEnvContext, useThrottle } from 'scbk-services';
import { formatUtils } from 'scbk-shared-module/utils';
import { TermsType } from './enums';
import { useTerms } from './hooks';

export const TermsComponent: React.FC<ReturnType<typeof useTerms>> = memo(
  ({ termsPopup, showTermsInfo, onCloseTerms }) => {
    const { t } = useTranslation();
    const currentEnv = useEnvContext().getEnv();

    const [pdfInfoIndex, setPdfInfoIndex] = useState<number>(0);

    const closeRef = useRef<boolean>(false);

    const handleClose = useThrottle(
      useCallback(() => {
        onCloseTerms();
      }, []),
    );

    const handleCancel = useThrottle(
      useCallback(() => {
        onCloseTerms(false);
      }, []),
    );

    const handleConfirm = useThrottle(
      useCallback(() => {
        if (closeRef.current) {
          return;
        }

        closeRef.current = true;
        setTimeout(() => {
          closeRef.current = false;
        }, 500);

        onCloseTerms(true);
      }, []),
    );

    // 안드로이드 물리 backKey 지정
    useBackKeyInterceptor({ enabled: true, type: 'popup' }, () => {
      handleClose();
      return false;
    });

    return (
      <>
        {(showTermsInfo?.termsType == TermsType.HTML_EXPLAIN ||
          showTermsInfo?.termsType == TermsType.HTML_VIEWER) && (
          <DialogWrapper
            title={showTermsInfo.prvsnNm}
            open={termsPopup.isOpen}
            setOpen={termsPopup.setOpen}
            isFull={true}
            dismissible={false}
            onClose={() => {
              handleClose();
            }}
            footer={
              showTermsInfo.cancelBtnLabel
                ? [
                    {
                      type: 'secondary',
                      title: showTermsInfo.cancelBtnLabel
                        ? showTermsInfo.cancelBtnLabel
                        : t('GenericDialog.002', '취소'),
                      handler: () => {
                        handleCancel();
                      },
                    },
                    {
                      type: 'primary',
                      title: showTermsInfo.okBtnLabel
                        ? showTermsInfo.okBtnLabel
                        : t('GenericDialog.001', '확인'),
                      handler: () => {
                        handleConfirm();
                      },
                    },
                  ]
                : [
                    {
                      type: 'primary',
                      title: showTermsInfo.okBtnLabel
                        ? showTermsInfo.okBtnLabel
                        : t('GenericDialog.001', '확인'),
                      handler: () => {
                        handleConfirm();
                      },
                    },
                  ]
            }
          >
            {/* 약관 트리 구조에서 wrapper div 있어야 디자인 적용 */}
            {showTermsInfo.depthFlag && (
              <div className="terms_wrap">
                <div className="content_wrap">
                  <div
                    dangerouslySetInnerHTML={{
                      __html: DOMPurify.sanitize(showTermsInfo.prvsnLongExpln ?? ''),
                    }}
                  />
                </div>
              </div>
            )}
            {!showTermsInfo.depthFlag && (
              <div
                dangerouslySetInnerHTML={{
                  __html: DOMPurify.sanitize(showTermsInfo.prvsnLongExpln ?? ''),
                }}
              />
            )}
          </DialogWrapper>
        )}

        {showTermsInfo?.termsType == TermsType.PDF_VIEWER && (
          <DialogWrapper
            title={showTermsInfo.pdfInfoList?.[pdfInfoIndex].title}
            open={termsPopup.isOpen}
            setOpen={termsPopup.setOpen}
            isFull={true}
            dismissible={false}
            onClose={() => {
              handleClose();
            }}
            footer={
              showTermsInfo.cancelBtnLabel
                ? [
                    {
                      type: 'secondary',
                      title: showTermsInfo.cancelBtnLabel
                        ? showTermsInfo.cancelBtnLabel
                        : t('GenericDialog.002', '취소'),
                      handler: () => {
                        handleCancel();
                      },
                    },
                    {
                      type: 'primary',
                      title: showTermsInfo.okBtnLabel
                        ? showTermsInfo.okBtnLabel
                        : t('GenericDialog.001', '확인'),
                      handler: () => {
                        handleConfirm();
                      },
                    },
                  ]
                : [
                    {
                      type: 'primary',
                      title: showTermsInfo.okBtnLabel
                        ? showTermsInfo.okBtnLabel
                        : t('GenericDialog.001', '확인'),
                      handler: () => {
                        handleConfirm();
                      },
                    },
                  ]
            }
          >
            <div className="viewer-typeA viewer-wrapper">
              <div className="viewer-pagination">
                <RoundButton
                  label={t('EdocViewComponent.002', '이전페이지')}
                  icon="ico-caret-left"
                  className="btn-prev"
                  onClick={() => {
                    if (pdfInfoIndex > 1) {
                      setPdfInfoIndex((prev) => prev - 1);
                    }
                  }}
                />
                <div className="viewer-current">
                  {formatUtils.addZero(pdfInfoIndex + 1, 2)}/
                  {formatUtils.addZero(showTermsInfo.pdfInfoList?.length ?? 0, 2)}
                </div>
                <RoundButton
                  label={t('EdocViewComponent.003', '다음페이지')}
                  icon="ico-caret-right"
                  className="btn-next"
                  onClick={() => {
                    if (pdfInfoIndex < (showTermsInfo.pdfInfoList?.length ?? 0) - 1) {
                      setPdfInfoIndex((prev) => prev + 1);
                    }
                  }}
                />
              </div>

              <div className="viewer-content-wrapper">
                <div className="contents">
                  <div className="scrollWrap">
                    <div className="agreement_wrap">
                      {Envs.LOCAL === currentEnv ? (
                        <>
                          <embed
                            src={showTermsInfo.pdfInfoList?.[pdfInfoIndex].pdfUrl}
                            width="100%"
                            height="350"
                          />
                        </>
                      ) : (
                        <embed
                          src={`https://drive.google.com/viewerng/viewer?embedded=true&url=${showTermsInfo.pdfInfoList?.[pdfInfoIndex].pdfUrl}`}
                          width="100%"
                          height="100%"
                        />
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </DialogWrapper>
        )}

        {showTermsInfo?.termsType == TermsType.PDF_PRODUCT && (
          <DialogWrapper
            title={showTermsInfo.prvsnNm}
            open={termsPopup.isOpen}
            setOpen={termsPopup.setOpen}
            isFull={true}
            dismissible={false}
            onClose={() => {
              handleClose();
            }}
            footer={
              showTermsInfo.cancelBtnLabel
                ? [
                    {
                      type: 'secondary',
                      title: showTermsInfo.cancelBtnLabel
                        ? showTermsInfo.cancelBtnLabel
                        : t('GenericDialog.002', '취소'),
                      handler: () => {
                        handleCancel();
                      },
                    },
                    {
                      type: 'primary',
                      title: showTermsInfo.okBtnLabel
                        ? showTermsInfo.okBtnLabel
                        : t('GenericDialog.001', '확인'),
                      handler: () => {
                        handleConfirm();
                      },
                    },
                  ]
                : [
                    {
                      type: 'primary',
                      title: showTermsInfo.okBtnLabel
                        ? showTermsInfo.okBtnLabel
                        : t('GenericDialog.001', '확인'),
                      handler: () => {
                        handleConfirm();
                      },
                    },
                  ]
            }
          >
            <div className="contents">
              <div className="scrollWrap">
                <div className="agreement_wrap">
                  {Envs.LOCAL === currentEnv ? (
                    <embed src={showTermsInfo.pdfUrl} width="100%" height="400" />
                  ) : (
                    <embed
                      src={`https://drive.google.com/viewerng/viewer?embedded=true&url=${showTermsInfo.pdfUrl}`}
                      width="100%"
                      height="100%"
                    />
                  )}
                </div>
              </div>
            </div>
          </DialogWrapper>
        )}
      </>
    );
  },
  (prev, next) => {
    return prev.showTermsInfo === next.showTermsInfo;
  },
);
